import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { API_BASE, fetchApi, getAuthHeaders } from '../../services/apiClient';

/** Re-run queue (Mackey item 4) and labelled set (Mackey item 5).
 *  The re-run is a command: preview first, then confirm one batch. */

interface Candidate {
  ticket_key: string;
  jira_created: string | null;
  last_run_at: string;
  model_id: string | null;
  determined: boolean | null;
  decision: 'run' | 'skip' | string;
  reason: string;
}

interface Preview {
  window_hours: number;
  recent_hours: number;
  day_cap: number;
  batch_cap: number;
  runs_today: number;
  remaining_today: number;
  parallel: number;
  cost_note: string;
  candidates: Candidate[];
}

interface RunRow {
  ticket_key: string;
  status: string;
  started_at: string | null;
  finished_at: string | null;
  error: string | null;
  model_id: string | null;
}

interface LabelRow {
  id: number;
  ticket_key: string;
  analysis_at: string;
  label: string;
  source: string;
  note: string | null;
  labelled_by: string;
  created_at: string;
}

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = { ...(await getAuthHeaders()), 'Content-Type': 'application/json' };
  const res = await fetchApi(`${API_BASE}${path}`, { ...init, headers });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new Error(body?.detail || body?.error || `Request failed (${res.status})`);
  return body as T;
}

export const RerunQueuePage: React.FC = () => {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [labels, setLabels] = useState<LabelRow[]>([]);
  const [form, setForm] = useState({ ticket_key: '', label: 'agreed', source: 'anthony', note: '' });
  const [error, setError] = useState<string | null>(null);

  const loadPreview = useCallback(() => {
    setError(null);
    call<Preview>('/admin/rerun-queue/preview')
      .then((p) => {
        setPreview(p);
        setSelected(new Set(p.candidates.filter((c) => c.decision === 'run').map((c) => c.ticket_key)));
      })
      .catch((e) => setError(e.message));
    call<LabelRow[]>('/admin/rca-labels').then(setLabels).catch(() => undefined);
  }, []);

  useEffect(loadPreview, [loadPreview]);

  // Poll the running batch every 15 s until every row is done or failed.
  useEffect(() => {
    if (!batchId) return;
    let live = true;
    const tick = () =>
      call<RunRow[]>(`/admin/rerun-queue/batches/${batchId}`)
        .then((r) => {
          if (!live) return;
          setRuns(r);
          if (r.length > 0 && r.every((x) => x.status === 'done' || x.status === 'failed')) {
            live = false;
            loadPreview();
          }
        })
        .catch(() => undefined);
    tick();
    const t = setInterval(() => live && tick(), 15000);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, [batchId, loadPreview]);

  const startBatch = () => {
    setError(null);
    call<{ batch_id: string }>('/admin/rerun-queue/run', {
      method: 'POST',
      body: JSON.stringify({ tickets: Array.from(selected), confirm: true }),
    })
      .then((r) => {
        setBatchId(r.batch_id);
        setConfirming(false);
      })
      .catch((e) => setError(e.message));
  };

  const saveLabel = () => {
    setError(null);
    call<LabelRow>('/admin/rca-labels', {
      method: 'POST',
      body: JSON.stringify({ ...form, note: form.note || null }),
    })
      .then(() => {
        setForm({ ...form, ticket_key: '', note: '' });
        call<LabelRow[]>('/admin/rca-labels').then(setLabels);
      })
      .catch((e) => setError(e.message));
  };

  const toggle = (k: string) => {
    const s = new Set(selected);
    if (s.has(k)) s.delete(k);
    else s.add(k);
    setSelected(s);
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6 text-sm">
      <div className="flex items-center gap-2">
        <h1 className="text-lg font-semibold">Re-run queue</h1>
        <button type="button" onClick={loadPreview} className="ml-2 text-blue-600 flex items-center gap-1">
          <RefreshCw size={14} /> Refresh preview
        </button>
      </div>
      {error && <div className="p-2 rounded bg-red-50 border border-red-200 text-red-700">{error}</div>}

      {preview && (
        <section className="space-y-2">
          <div className="text-gray-600">
            Tickets created in the last {preview.window_hours} h. Runs today: {preview.runs_today} of {preview.day_cap}
            {' · '}batch cap {preview.batch_cap}{' · '}{preview.parallel} at a time{' · '}skip if analysed within {preview.recent_hours} h.
          </div>
          <div className="text-xs text-gray-500">{preview.cost_note}</div>
          <div className="overflow-x-auto">
            <table className="min-w-full border text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="p-1 border"></th>
                  <th className="p-1 border text-left">Ticket</th>
                  <th className="p-1 border text-left">Created (JIRA)</th>
                  <th className="p-1 border text-left">Last run</th>
                  <th className="p-1 border text-left">Model</th>
                  <th className="p-1 border text-left">Determined</th>
                  <th className="p-1 border text-left">Decision</th>
                </tr>
              </thead>
              <tbody>
                {preview.candidates.map((c) => (
                  <tr key={c.ticket_key} className={c.decision === 'run' ? '' : 'text-gray-400'}>
                    <td className="p-1 border text-center">
                      {c.decision === 'run' && (
                        <input type="checkbox" checked={selected.has(c.ticket_key)} onChange={() => toggle(c.ticket_key)} />
                      )}
                    </td>
                    <td className="p-1 border font-mono">{c.ticket_key}</td>
                    <td className="p-1 border">{fmt(c.jira_created)}</td>
                    <td className="p-1 border">{fmt(c.last_run_at)}</td>
                    <td className="p-1 border">{c.model_id ?? 'Model not recorded'}</td>
                    <td className="p-1 border">{c.determined == null ? '—' : c.determined ? 'yes' : 'no'}</td>
                    <td className="p-1 border">{c.decision === 'run' ? 'run' : `skip - ${c.reason}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!confirming ? (
            <button
              type="button"
              disabled={selected.size === 0}
              onClick={() => setConfirming(true)}
              className="px-3 py-1 rounded bg-blue-600 text-white disabled:bg-gray-300"
            >
              Re-run {selected.size} ticket(s)…
            </button>
          ) : (
            <div className="p-2 border rounded bg-amber-50 space-x-2">
              <span>
                Re-run {selected.size} ticket(s) now? Each run calls the AI model and replaces the stored analysis
                (analyst entities are kept).
              </span>
              <button type="button" onClick={startBatch} className="px-3 py-1 rounded bg-blue-600 text-white">
                Yes, start the batch
              </button>
              <button type="button" onClick={() => setConfirming(false)} className="px-3 py-1 rounded border">
                Cancel
              </button>
            </div>
          )}
        </section>
      )}

      {batchId && (
        <section className="space-y-1">
          <h2 className="font-semibold">Batch {batchId.slice(0, 8)}</h2>
          <ul className="text-xs space-y-0.5">
            {runs.map((r) => (
              <li key={r.ticket_key}>
                <span className="font-mono">{r.ticket_key}</span> - {r.status}
                {r.model_id ? ` (${r.model_id})` : ''}
                {r.error ? ` - ${r.error}` : ''}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">Labelled set</h2>
        <div className="text-xs text-gray-500">
          A label judges the CURRENT analysis of a ticket. A re-run makes a new analysis, which the label does not cover.
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <input
            className="border rounded px-2 py-1 font-mono"
            placeholder="Ticket, e.g. FMSC-3696"
            value={form.ticket_key}
            onChange={(e) => setForm({ ...form, ticket_key: e.target.value })}
          />
          <select className="border rounded px-2 py-1" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}>
            <option value="agreed">agreed</option>
            <option value="false_green">false_green</option>
            <option value="partly">partly</option>
          </select>
          <input
            className="border rounded px-2 py-1"
            placeholder="Source, e.g. anthony or mesh:968"
            value={form.source}
            onChange={(e) => setForm({ ...form, source: e.target.value })}
          />
          <input
            className="border rounded px-2 py-1 flex-1 min-w-[12rem]"
            placeholder="Note (optional)"
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
          />
          <button type="button" onClick={saveLabel} disabled={!form.ticket_key.trim()} className="px-3 py-1 rounded bg-blue-600 text-white disabled:bg-gray-300">
            Save label
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full border text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-1 border text-left">Ticket</th>
                <th className="p-1 border text-left">Analysis of</th>
                <th className="p-1 border text-left">Label</th>
                <th className="p-1 border text-left">Source</th>
                <th className="p-1 border text-left">Note</th>
                <th className="p-1 border text-left">By</th>
              </tr>
            </thead>
            <tbody>
              {labels.map((l) => (
                <tr key={l.id}>
                  <td className="p-1 border font-mono">{l.ticket_key}</td>
                  <td className="p-1 border">{fmt(l.analysis_at)}</td>
                  <td className="p-1 border">{l.label}</td>
                  <td className="p-1 border">{l.source}</td>
                  <td className="p-1 border">{l.note ?? ''}</td>
                  <td className="p-1 border">{l.labelled_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};
